package com.zq.webproject.utils

/**
 * 本地存储接口 - expect声明
 * 不同平台提供不同的实现
 */
expect object LocalStorage {
    fun setItem(key: String, value: String)
    fun getItem(key: String): String?
    fun removeItem(key: String)
}

/**
 * Token管理器
 * 负责Token的存储和获取
 * 使用LocalStorage实现跨平台持久化存储
 */
object TokenManager {
    private const val TOKEN_KEY = "user_token"
    private const val USERNAME_KEY = "user_username"
    private const val REFERRAL_CODE_KEY = "user_referral_code"
    private const val PENDING_INVITATION_CODE_KEY = "pending_invitation_code"  // 用于保存待注册的邀请码
    private const val REMEMBERED_PHONE_KEY = "remembered_phone"  // 记住的手机号
    private const val REMEMBERED_PASSWORD_KEY = "remembered_password"  // 记住的密码
    private const val REMEMBER_ME_KEY = "remember_me"  // 是否记住密码

    // 内存缓存
    private var currentToken: String? = null
    private var currentUsername: String? = null
    private var currentReferralCode: String? = null
    private var pendingInvitationCode: String? = null  // 待注册的邀请码缓存

    init {
        // 初始化时从本地存储加载Token
        println("TokenManager - 初始化，尝试从本地存储加载Token")
        loadFromStorage()
    }

    /**
     * 从本地存储加载Token
     */
    private fun loadFromStorage() {
        try {
            val token = LocalStorage.getItem(TOKEN_KEY)
            val username = LocalStorage.getItem(USERNAME_KEY)
            val referralCode = LocalStorage.getItem(REFERRAL_CODE_KEY)

            if (token != null && token.isNotEmpty()) {
                currentToken = token
                currentUsername = username
                currentReferralCode = referralCode
                println("TokenManager - 从本地存储加载Token成功: 用户=$username, 邀请码=$referralCode")
            } else {
                println("TokenManager - 本地存储中没有保存的Token")
            }
        } catch (e: Exception) {
            println("TokenManager - 从本地存储加载Token失败: ${e.message}")
        }
    }

    /**
     * 保存Token到本地存储
     */
    fun saveToken(token: String, username: String? = null, referralCode: String? = null) {
        println("TokenManager - 保存Token: $token, 用户: $username, 邀请码: $referralCode")

        currentToken = token
        currentUsername = username
        currentReferralCode = referralCode

        try {
            LocalStorage.setItem(TOKEN_KEY, token)
            if (username != null) {
                LocalStorage.setItem(USERNAME_KEY, username)
            }
            if (referralCode != null) {
                LocalStorage.setItem(REFERRAL_CODE_KEY, referralCode)
            }
            println("TokenManager - Token、用户名和邀请码已保存到本地存储（持久化）")
        } catch (e: Exception) {
            println("TokenManager - 保存到本地存储失败: ${e.message}")
        }
    }

    /**
     * 获取Token
     */
    fun getToken(): String? {
        // 如果内存中没有，尝试从存储加载
        if (currentToken == null) {
            loadFromStorage()
        }
        println("TokenManager - 获取Token: $currentToken")
        return currentToken
    }

    /**
     * 获取用户名
     */
    fun getUsername(): String? {
        // 如果内存中没有，尝试从存储加载
        if (currentUsername == null) {
            loadFromStorage()
        }
        return currentUsername
    }

    /**
     * 获取邀请码
     */
    fun getReferralCode(): String? {
        // 如果内存中没有，尝试从存储加载
        if (currentReferralCode == null) {
            loadFromStorage()
        }
        return currentReferralCode
    }

    /**
     * 保存待注册的邀请码（扫码进入时保存）
     * 用于在注册前临时保存邀请码，注册成功后自动清除
     */
    fun savePendingInvitationCode(code: String) {
        println("TokenManager - 保存待注册邀请码: $code")
        pendingInvitationCode = code
        try {
            LocalStorage.setItem(PENDING_INVITATION_CODE_KEY, code)
            println("TokenManager - 待注册邀请码已持久化保存")
        } catch (e: Exception) {
            println("TokenManager - 保存待注册邀请码失败: ${e.message}")
        }
    }

    /**
     * 获取待注册的邀请码
     * 扫码进入后可随时获取，不会因为页面刷新而丢失
     */
    fun getPendingInvitationCode(): String? {
        // 如果内存中没有，尝试从存储加载
        if (pendingInvitationCode == null) {
            try {
                pendingInvitationCode = LocalStorage.getItem(PENDING_INVITATION_CODE_KEY)
                println("TokenManager - 从本地存储加载待注册邀请码: $pendingInvitationCode")
            } catch (e: Exception) {
                println("TokenManager - 加载待注册邀请码失败: ${e.message}")
            }
        }
        return pendingInvitationCode
    }

    /**
     * 清除待注册的邀请码（注册成功后调用）
     */
    fun clearPendingInvitationCode() {
        println("TokenManager - 清除待注册邀请码")
        pendingInvitationCode = null
        try {
            LocalStorage.removeItem(PENDING_INVITATION_CODE_KEY)
            println("TokenManager - 已从本地存储清除待注册邀请码")
        } catch (e: Exception) {
            println("TokenManager - 清除待注册邀请码失败: ${e.message}")
        }
    }

    /**
     * 登出回调函数
     * 当 Token 失效时会被调用
     */
    private var onTokenInvalidCallback: (() -> Unit)? = null

    /**
     * 设置 Token 失效回调
     */
    fun setOnTokenInvalidCallback(callback: () -> Unit) {
        onTokenInvalidCallback = callback
    }

    /**
     * 触发 Token 失效回调
     */
    fun triggerTokenInvalid() {
        println("TokenManager - Token失效，触发登出回调")
        onTokenInvalidCallback?.invoke()
    }

    /**
     * 清除Token
     */
    fun clearToken() {
        println("TokenManager - 清除Token")

        currentToken = null
        currentUsername = null
        currentReferralCode = null

        try {
            LocalStorage.removeItem(TOKEN_KEY)
            LocalStorage.removeItem(USERNAME_KEY)
            LocalStorage.removeItem(REFERRAL_CODE_KEY)
            println("TokenManager - 已从本地存储清除Token、用户名和邀请码")
        } catch (e: Exception) {
            println("TokenManager - 清除本地存储失败: ${e.message}")
        }
    }

    /**
     * 检查是否已登录
     */
    fun isLoggedIn(): Boolean {
        // 如果内存中没有，尝试从存储加载
        if (currentToken == null) {
            loadFromStorage()
        }

        val loggedIn = currentToken != null
        println("TokenManager - 检查登录状态: $loggedIn")
        return loggedIn
    }

    /**
     * 保存记住的登录凭据
     */
    fun saveRememberedCredentials(phone: String, password: String) {
        println("TokenManager - 保存记住的登录凭据: phone=$phone")
        try {
            LocalStorage.setItem(REMEMBERED_PHONE_KEY, phone)
            LocalStorage.setItem(REMEMBERED_PASSWORD_KEY, password)
            LocalStorage.setItem(REMEMBER_ME_KEY, "true")
            println("TokenManager - 登录凭据已保存")
        } catch (e: Exception) {
            println("TokenManager - 保存登录凭据失败: ${e.message}")
        }
    }

    /**
     * 获取记住的手机号
     */
    fun getRememberedPhone(): String? {
        return try {
            LocalStorage.getItem(REMEMBERED_PHONE_KEY)
        } catch (e: Exception) {
            println("TokenManager - 获取记住的手机号失败: ${e.message}")
            null
        }
    }

    /**
     * 获取记住的密码
     */
    fun getRememberedPassword(): String? {
        return try {
            LocalStorage.getItem(REMEMBERED_PASSWORD_KEY)
        } catch (e: Exception) {
            println("TokenManager - 获取记住的密码失败: ${e.message}")
            null
        }
    }

    /**
     * 获取是否记住密码
     */
    fun getRememberMe(): Boolean {
        return try {
            LocalStorage.getItem(REMEMBER_ME_KEY) == "true"
        } catch (e: Exception) {
            println("TokenManager - 获取记住密码状态失败: ${e.message}")
            false
        }
    }

    /**
     * 清除记住的登录凭据
     */
    fun clearRememberedCredentials() {
        println("TokenManager - 清除记住的登录凭据")
        try {
            LocalStorage.removeItem(REMEMBERED_PHONE_KEY)
            LocalStorage.removeItem(REMEMBERED_PASSWORD_KEY)
            LocalStorage.removeItem(REMEMBER_ME_KEY)
            println("TokenManager - 已清除记住的登录凭据")
        } catch (e: Exception) {
            println("TokenManager - 清除记住的登录凭据失败: ${e.message}")
        }
    }
}